<!-- 会員情報登録・変更コンポーネント -->
<template>
    <div>
        <dl class="formContainer">
            <template v-if="isEmailField">
                <field-title :is-required="true"
                             :valid="!$v.email.$invalid">メールアドレス</field-title>
                <dd :class="{error: $v.email.$error}">
                    <template v-if="$v.email.$error">
                        <span v-if="!$v.email.required" class="errNote">メールアドレスを入力してください。</span>
                    </template>
                    <template v-if="$v.email.$invalid">
                        <span v-if="!$v.email.signupEmailRule" class="errNote">メールアドレスの書式が不正です。（先頭は半角英数以外使用できません。使用できる記号は@_/.-です。ただし、ドットが連続するもの、@の直前にドットがあるものなど、RFC非準拠のものは使用できません。）</span>
                    </template>
                    <input v-model="$v.email.$model"
                           @input="setUserInfoIdAndEmail"
                           name="email"
                           type="email"
                           id="email"
                           class="regular">
                </dd>
            </template>

            <field-title :is-required="true"
                         :valid="!$v.jleague_id.$invalid"
                         :form-name="formName">ＪリーグID</field-title>
            <jleague-id-field @set-user-info-patch="setUserInfoPatch"
                              :validate="$v.jleague_id"
                              :is-xhr="isXhr"
                              :is-unique="isUnique"
                              :disabled="isSignup"
                              :form-name="formName"
                              :is-duplicate-jleague-id="isDuplicateJleagueId"
                              :editable-jleague-id="editableJleagueId"
                              :isEmailToken="isEmailToken"
                              :original_jleague_id="original_jleague_id"
                              :activateJleagueId="activateJleagueId"
            ></jleague-id-field>

            <template v-if="isSignup">
                <field-title :is-required="true"
                             :valid="!$v.password.$invalid">パスワード</field-title>
                <password-field @set-user-info-patch="setUserInfoPatch"
                                :validate="$v.password"></password-field>
            </template>

            <field-title :is-required="!isMinimum"
                         :valid="!$v.birthday_ymd.$invalid">生年月日</field-title>
            <birthday-field @set-user-info-patch="setUserInfoPatch"
                            :validate="$v.birthday_ymd"></birthday-field>

            <template v-if="!isSignup || isSignupFull">
                <field-title :is-required="isFull"
                             :valid="validMemberName">氏名</field-title>
                <member-name-field @set-user-info-patch="setUserInfoPatch"
                                   @last-name-handler="lastNameHandler"
                                   @first-name-handler="firstNameHandler"
                                   :validate-last-name="$v.member_last_nm"
                                   :validate-first-name="$v.member_first_nm"></member-name-field>

                <field-title :is-required="isFull"
                             :valid="validMemberNameKana">氏名カナ</field-title>
                <member-kana-name-field @set-user-info-patch="setUserInfoPatch"
                                        :validate-last-name="$v.member_last_nm_kana"
                                        :validate-first-name="$v.member_first_nm_kana"></member-kana-name-field>
            </template>

            <dt>性別</dt>
            <sex-field @set-user-info-patch="setUserInfoPatch"
                       :validate="$v.sex_kbn"></sex-field>

            <template v-if="!isSignup || isSignupFull">
                <field-title :is-required="isFull"
                             :valid="!$v.postcode.$invalid">郵便番号</field-title>
                <postcode-field @set-user-info-patch="setUserInfoPatch"
                                :validate="$v.postcode"></postcode-field>
            </template>

            <field-title :is-required="!isMinimum"
                         :valid="!$v.prefecture.$invalid">都道府県</field-title>
            <prefecture-field @set-user-info-patch="setUserInfoPatch"
                              :validate="$v.prefecture"></prefecture-field>

            <template v-if="!isSignup || isSignupFull">
                <field-title :is-required="isFull"
                             :valid="!$v.address_city.$invalid">市区町村</field-title>
                <address-city-field @set-user-info-patch="setUserInfoPatch"
                                    :validate="$v.address_city"></address-city-field>

                <field-title :is-required="isFull"
                             :valid="!$v.address_street.$invalid">町域番地</field-title>
                <address-street-field @set-user-info-patch="setUserInfoPatch"
                                      :validate="$v.address_street"></address-street-field>

                <dt>建物号室</dt>
                <address-building-field @set-user-info-patch="setUserInfoPatch"
                                        :validate="$v.address_building"></address-building-field>

                <field-title :is-required="isFull"
                             :valid="!$v.tel_no_fixed.$invalid">電話番号</field-title>
                <phone-number-field @set-user-info-patch="setUserInfoPatch"
                                    :validate="$v.tel_no_fixed"></phone-number-field>
            </template>

            <template v-if="isDisplayAgreementCheck">
                <field-title :is-required="true"
                             :valid="agreement">利用規約</field-title>
                <agreement-field @check-agreement="checkAgreement"
                                 :checked="agreement"></agreement-field>
            </template>
        </dl>

        <div class="btnSpace">
            <xhr-button v-if="isSignup"
                        @on-click="next"
                        :invalid="invalid"
                        :is-xhr="isXhr"
                        :is-error="isError"
                        :activateJleagueId="isDuplicateJleagueId ? !activateJleagueId : false"
                        gtm-event-id="regist_next_tap"></xhr-button>
            <xhr-button v-else
                        @on-click="next"
                        :invalid="invalid"
                        :is-xhr="isXhr"
                        :is-error="isError"
                        :activateJleagueId="!isUnique ? !activateJleagueId : false"></xhr-button>

        </div>
    </div>
</template>

<script>
import Vue from 'vue'
import Vuelidate from 'vuelidate'
import * as AutoKana from 'vanilla-autokana'
import { JleagueApiClient } from '../../../libs/client'
import { required, requiredIf, requiredUnless, numeric, minLength, maxLength, sameAs } from 'vuelidate/lib/validators'
import { signupEmailRule, jleagueIdRule, passwordRule, dateRule, birthdayRule, zenkakuNameRule, zenkakuRule, zenhankakuRule, zenkakuMaxLength, zenkakuKataKanaRule, phoneNumberRule } from '../../../validation/rules'
import { EnumClientDisplayPattern } from '../../mixin/utility'
import UserInfoFieldTitle from './UserInfoFieldTitle'
import UserInfoJleagueIdField from './UserInfoJleagueIdField'
import UserInfoPasswordField from './UserInfoPasswordField'
import UserInfoBirthdayField from './UserInfoBirthdayField'
import UserInfoMemberNameField from './UserInfoMemberNameField'
import UserInfoMemberKanaNameField from './UserInfoMemberKanaNameField'
import UserInfoSexField from './UserInfoSexField'
import UserInfoPostcodeField from './UserInfoPostcodeField'
import UserInfoPrefectureField from './UserInfoPrefectureField'
import UserInfoAddressCityField from './UserInfoAddressCityField'
import UserInfoAddressStreetField from './UserInfoAddressStreetField'
import UserInfoAddressBuildingField from './UserInfoAddressBuildingField'
import UserInfoPhoneNumberField from './UserInfoPhoneNumberField'
import UserInfoAgreementField from './UserInfoAgreementField'
import XhrButton from '../XhrButton'

Vue.use(Vuelidate)

let autoKanaLastName;
let autoKanaFirstName;

export default {
    name: 'UserInfoForm',
    components: {
        'field-title': UserInfoFieldTitle,
        'jleague-id-field': UserInfoJleagueIdField,
        'password-field': UserInfoPasswordField,
        'birthday-field': UserInfoBirthdayField,
        'member-name-field': UserInfoMemberNameField,
        'member-kana-name-field': UserInfoMemberKanaNameField,
        'sex-field': UserInfoSexField,
        'postcode-field': UserInfoPostcodeField,
        'prefecture-field': UserInfoPrefectureField,
        'address-city-field': UserInfoAddressCityField,
        'address-street-field': UserInfoAddressStreetField,
        'address-building-field': UserInfoAddressBuildingField,
        'phone-number-field': UserInfoPhoneNumberField,
        'agreement-field': UserInfoAgreementField,
        'xhr-button': XhrButton,
    },
    data: function(){
        return {
            userInfoPatch: '',
            original_jleague_id: '',
            current_jleague_id:'',
            email: '',
            jleague_id: '',
            password: '',
            birthday_ymd: '',
            member_last_nm: '',
            member_first_nm: '',
            member_last_nm_kana: '',
            member_first_nm_kana: '',

            sex_kbn: '',
            postcode: '',
            prefecture: '',
            address_city: '',
            address_street: '',
            address_building: '',

            tel_no_fixed: '',
            agreement: false,

            onConfirm: false,
            isUnique: true,
            isError: false
        }
    },
    props: {
        userinfo: {
            type: Object,
            required: true
        },
        formName: {
            type: String,
            default: ''
        },
        displayPattern: {
            type: String,
            default: EnumClientDisplayPattern.Full
        },
        isDisplayAgreementCheck: {
            type: Boolean,
            required: true
        },
        isEmailField: {
            type: Boolean,
            default: false
        },
        isXhr: {
            type: Boolean,
            default: false
        },
        step: {
            type: String,
            default: ''
        },
        // FLPサインアップのときクエリパラメータ (token) に含まれている値
        flpToken: {
            type: String,
            default: ''
        },
        // メールアドレス経由で登録し、 JリーグIDが重複する場合
        isDuplicateJleagueId: {
            type: Boolean,
            default: false
        },
        editableJleagueId: {
            type: Boolean,
            default: false
        },
        isEmailToken: {
            type: Boolean,
            required: false,
            default: false
        }
    },
    validations: {
        email: {
            required: requiredIf(function(){
                return this.isEmailField;
            }),
            signupEmailRule
        },
        jleague_id: {
            required,
            minLength: minLength(6),
            maxLength: maxLength(128),
            jleagueIdRule
            // isUnique: async function(value){
            //     // 確認ボタンを押下したときチェックする
            //     if (value === '' || !this.onConfirm) return true;
            //
            //     // TODO: JリーグIDに変更があればユニークチェックAPIを実行する
            //     // if (value === ) return true;
            //
            //     const result = await this.checkAvailability(value);
            //     switch (result) {
            //         case 'AVAILABLE':
            //             return true;
            //         case 'UNAVAILABLE':
            //             return false;
            //         default:
            //             this.isError = true;
            //             this.showMessage(result);
            //             return true;
            //     }
            // }
        },
        password: {
            required: requiredIf(function(){
                return this.isSignup;
            }),
            minLength: minLength(8),
            maxLength: maxLength(20),
            passwordRule
        },
        birthday_ymd: {
            required: requiredUnless(function(){
                return this.isMinimum;
            }),
            dateRule,
            birthdayRule,
        },
        member_last_nm: {
            required: requiredIf(function(){
                return this.isFull || !this.completeMemberName;
            }),
            zenkakuNameRule,
            zenhankakuRule,
            zenkakuMaxLength: zenkakuMaxLength(10),
        },
        member_first_nm: {
            required: requiredIf(function(){
                return this.isFull || !this.completeMemberName;
            }),
            zenkakuNameRule,
            zenhankakuRule,
            zenkakuMaxLength: zenkakuMaxLength(10),
        },
        member_last_nm_kana: {
            required: requiredIf(function(){
                return this.isFull || !this.completeMemberNameKana;
            }),
            zenkakuKataKanaRule,
            maxLength: maxLength(20),
        },
        member_first_nm_kana: {
            required: requiredIf(function(){
                return this.isFull || !this.completeMemberNameKana;
            }),
            zenkakuKataKanaRule,
            maxLength: maxLength(20),
        },
        sex_kbn: {
            required: requiredUnless(function(){
                return this.isMinimum;
            })
        },
        postcode: {
            required: requiredIf(function(){
                return this.isFull;
            }),
            numeric,
            minLength: minLength(7),
            maxLength: maxLength(7)
        },
        prefecture: {
            required: requiredUnless(function(){
                return this.isMinimum;
            })
        },
        address_city: {
            required: requiredIf(function(){
                return this.isFull;
            }),
            zenkakuRule,
            zenhankakuRule,
            zenkakuMaxLength: zenkakuMaxLength(26),
        },
        address_street: {
            required: requiredIf(function(){
                return this.isFull;
            }),
            zenhankakuRule,
            zenkakuMaxLength: zenkakuMaxLength(26),
        },
        address_building: {
            zenhankakuRule,
            zenkakuMaxLength: zenkakuMaxLength(26),
        },
        tel_no_fixed: {
            required: requiredIf(function(){
                return this.isFull;
            }),
            phoneNumberRule
        }
    },
    computed: {
        isSignup: function(){
            return this.formName === 'signup';
        },
        isMinimum: function(){
            return this.displayPattern === EnumClientDisplayPattern.Minimum;
        },
        isNormal: function(){
            return this.displayPattern === EnumClientDisplayPattern.Normal;
        },
        isFull: function(){
            return this.displayPattern === EnumClientDisplayPattern.Full;
        },
        isSignupFull: function(){
            return this.isSignup && this.isFull;
        },
        isUpdatedJleagueId: function(){
            // 現在利用しているjleague_idのみ、falseを返してユニークチェックを行わない
            if (this.current_jleague_id !== this.jleague_id) {
                return true
            } else {
                return false
            }
        },
        validMemberName: function(){
            // 氏名が必須 ∧ valid のときアイコンを表示する
            return this.isFull
                && !this.$v.member_last_nm.$invalid
                && !this.$v.member_first_nm.$invalid;
        },
        validMemberNameKana: function(){
            // 氏名カナが必須 ∧ valid のときアイコンを表示する
            return this.isFull
                && !this.$v.member_last_nm_kana.$invalid
                && !this.$v.member_first_nm_kana.$invalid;
        },
        completeMemberName: function(){
            // 姓または名のどちらかしか1つしか入力されていないとき false を返す
            return [this.member_last_nm, this.member_first_nm].filter(function(value){
                return value !== '';
            }).length !== 1;
        },
        completeMemberNameKana: function(){
            // 姓または名のどちらかしか1つしか入力されていないとき false を返す
            return [this.member_last_nm_kana, this.member_first_nm_kana].filter(function(value){
                return value !== '';
            }).length !== 1;
        },
        invalid: function(){
            return this.$v.$invalid || (this.isDisplayAgreementCheck && !this.agreement);
        },
        activateJleagueId: function() {
            // 前回入力したjleague_idと入力中のjleague_idが異なればボタン活性
            if (this.original_jleague_id !== this.jleague_id) {
                return true
            }
            // 初回登録時かつjleague_idが重複していたらflase(非活性)
            if (this.isSignup && this.isDuplicateJleagueId) {
                return false
            } 
            // 編集時に自身の現在使用しているjleague_idの場合は活性
            if (this.current_jleague_id === this.jleague_id) {
                this.isUnique = true;
                return true
            }     
            return false
        }
    },
    watch: {
        postcode: function(){
            // 初期化
            this.userInfoPatch.post_cd1 = this.userInfoPatch.post_cd2 = '';

            // 3桁-4桁に分割
            const postcode = this.postcode;
            this.userInfoPatch.post_cd1 = postcode.substr(0, 3);
            this.userInfoPatch.post_cd2 = postcode.substr(3, 4);
        },
        prefecture: function(){
            this.userInfoPatch.address_state = this.prefecture;
        }
    },
    mounted: function(){
        // ＪリーグID新規登録以外またはＪリーグID新規登録でフル表示のときのみ入力補完を有効にする
        if (!this.isSignup || this.isSignupFull) {
            this.postcodeObserver();
            this.autokanaBinder();
        }
    },
    created: function(){
        this.original_jleague_id = this.userinfo.jleague_id;
        // 新規登録時、jleague_idが重複しているはcurrent_jleague_idを''にする
        if (!this.isSignup && !this.isDuplicateJleagueId) {
            this.current_jleague_id = this.userinfo.jleague_id;
        }
    
        this.userInfoPatch = this.userinfo;

        this.postcode = [this.userInfoPatch.post_cd1, this.userInfoPatch.post_cd2].join('');
        this.prefecture = this.userInfoPatch.address_state;

        const self = this;
        Object.keys(this.userInfoPatch).forEach(function(key){
            const value = self.userInfoPatch[key];
            if (self.hasOwnProperty(key) && value !== '') {
                self[key] = value;
            }
        });
    },
    methods: {
        postcodeObserver: function(){
            const postcodeJp = new postcodejp.address.AutoComplementService(POSTCODEJP_API_KEY);
            postcodeJp.setZipTextbox('js-input-zip');
            postcodeJp.add(new postcodejp.address.StateTextbox('js-address-state'));
            postcodeJp.add(new postcodejp.address.TownTextbox('js-address-city'));
            postcodeJp.add(new postcodejp.address.StreetTextbox('js-address-street'));
            postcodeJp.observe();
        },
        autokanaBinder: function(){
            const option = { katakana: true };
            autoKanaLastName = AutoKana.bind('#js-member-last-name', '#js-member-last-name-kana', option);
            autoKanaFirstName = AutoKana.bind('#js-member-first-name', '#js-member-first-name-kana', option);
        },
        lastNameHandler: function(){
            this.member_last_nm_kana = autoKanaLastName.getFurigana();
            this.userInfoPatch.member_last_nm_kana = this.member_last_nm_kana;
        },
        firstNameHandler: function(){
            this.member_first_nm_kana = autoKanaFirstName.getFurigana();
            this.userInfoPatch.member_first_nm_kana = this.member_first_nm_kana;
        },
        /**
         * SNSまたはFLPサインアップでメールアドレスを入力したとき、関連するdataプロパティに値を設定する
         * - email
         * - jleague_id
         * - userInfoPatch.email
         * - userInfoPatch.jleague_id
         * - userInfoPatch.mail_address
         * @param {Event} ev
         */
        setUserInfoIdAndEmail: function(ev){
            // 大文字で入力されたメールアドレスは小文字に変換する
            const value = ev.target.value;
            this.email = value;
            // JリーグID重複時は、email = jleague_id にしない。
            if(!this.editableJleagueId || !this.isUnique) {
                this.jleague_id = value;
                this.userInfoPatch.jleague_id = value;
            }
            this.userInfoPatch.email = value;
            this.userInfoPatch.mail_address = value;
        },
        setUserInfoPatch: function(key, value){
            this.userInfoPatch[key] = value;
        },
        checkAgreement: function(value){
            this.agreement = value;
        },
        checkAvailability: async function(){
            const self = this;

            // ボタンのラベルを「確認」から「通信中」へ変更する
            this.setXhr(true);

            // APIを実行
            const client = new JleagueApiClient();
            return await client.checkAvailability(this.jleague_id, this.flpToken)
                .then(function(response){
                    // 入力されたJリーグIDが使用可能なとき
                    return 'AVAILABLE';
                })
                .catch(function(error){
                    const response = error.response;
                    switch (response.status) {
                        case 409:
                            return 'UNAVAILABLE';
                        case 400:
                        case 401:
                        case 403:
                        case 500:
                            // pathとエラーコードをクエリストリングに追加してエラー画面に遷移
                            location.href = self.addUrlParams('https://login2.jleague.jp/error', {
                                path: 'signup',
                                error_code: `02${response.status}`,
                            });
                            break;
                        default:
                            // pathをクエリストリングに追加してエラー画面に遷移
                            location.href = self.addUrlParams('https://login2.jleague.jp/error', {
                                path: 'signup',
                            });
                            break;
                    }
                })
                .finally(function(){
                    self.setXhr(false);
                });
        },
        next: async function(){
            if (this.onConfirm) return;
            this.onConfirm = true;
            // バリデーション実行
            this.$v.$touch();
            if (this.$v.$invalid) {
                this.onConfirm = false;
                return
            };

            // JリーグIDのユニークチェックが必要とき
            // - 会員情報更新画面でJリーグIDを変更した
            // - FLPサインアップ画面でメールアドレスを入力し、JリーグIDが変更された
            if (this.isUpdatedJleagueId) {
                // JリーグIDに変更があればユニークチェックAPIを実行する
                const result = await this.checkAvailability();
                switch (result) {
                    case 'AVAILABLE':
                        // 確認画面を表示する
                        this.original_jleague_id = this.userinfo.jleague_id;
                        this.isUnique = true;
                        this.isDuplicateJleagueId = false;
                        break;
                    case 'UNAVAILABLE':
                        this.isUnique = false;
                        this.isDuplicateJleagueId = true;
                        this.original_jleague_id = this.userinfo.jleague_id;
                        this.activateJleagueId = false;
                        // エラーが出たことが分かりにくいので、トップまで戻る
                        window.scrollTo(0, 0);
                        break;
                    default:
                        this.isError = true;
                        this.showMessage(result);
                }
            } else {
                this.isUnique = true;
            }

            // JリーグIDがユニークだったとき、画面を切り替える
            if (this.isUnique) {
                // サインアップのときパスワードをセットする
                if (this.isSignup) this.$emit('set-password', this.password);
                this.$emit('next', this.step, this.userInfoPatch);
            }
            this.onConfirm = false;
        },
        setXhr: function(status){
            this.$emit('set-xhr', status);
        },
        showMessage: function(message){
            this.$emit('alert-message', message);
        },
        /**
         * URLにクエリストリングを追加して返す
         * @param {string} url ベースになるURL
         * @param {object} params URLに追加するクエリストリングのkeyとvalue
         * @returns {string}
         */
        addUrlParams: function(url, params){
            const u = new URL(url);
            Object.keys(params).forEach(function(key){
                u.searchParams.set(key, params[key]);
            });

            return u.toString();
        },
    }
}
</script>
